// Need to use the React-specific entry point to import createApi
import {fetchAuthSession} from 'aws-amplify/auth';
import axios from 'axios';
import type {
  FileResponse,
  FormPackageRequest,
  FormRequest,
  FormResponse,
  FormSubmissionExportRequest,
  GetFormListRequest,
  GetFormListRequestV2,
  GetFormRelatedTasksListRequestV2,
  GetListResponseOfFormRelatedTaskSimpleResponse,
  GetListResponseOfFormSimpleResponse,
  GetListResponseOfUserReferencedFormResponse,
  UserReferencedFormsRequest,
} from 'src/api/ProactApiClient';
import {formApiClient} from 'src/api/ProactApiClientInstances';
import {createFormMutationFn} from 'src/app/forms/offline/mutations';
import type {OfflineFormCreateRequest} from 'src/app/forms/offline/queuedTransition';
import {formListV2QueryFn} from 'src/app/forms/offline/reads';
import {isOfflineQueuedResult} from 'src/offline/offlineMutation';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export interface FormPackageManifestItem {
  key: string;
  displayName: string;
  sourceGroup: string;
  originLabel: string;
  available: boolean;
}

export interface FormPackageManifestResponse {
  formId: number;
  internalIdentifier: string | null;
  items: FormPackageManifestItem[];
}

export interface PackageFormRequest extends FormPackageRequest {
  formId: number;
  timeZone: string;
}

// Define a service using a base URL and expected endpoints
export const formApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getFormList: builder.query<
      GetListResponseOfFormSimpleResponse,
      GetFormListRequest
    >({
      queryFn: clientQuery(params => formApiClient.list(params)),
      providesTags: ['Forms'],
    }),
    getFormListV2: builder.query<
      GetListResponseOfFormSimpleResponse,
      GetFormListRequestV2
    >({
      queryFn: formListV2QueryFn(params => formApiClient.listV2(params)),
      providesTags: ['Forms'],
    }),
    getFormRelatedTasksListV2: builder.query<
      GetListResponseOfFormRelatedTaskSimpleResponse,
      GetFormRelatedTasksListRequestV2
    >({
      queryFn: clientQuery(params => formApiClient.listRelatedTasksV2(params)),
      providesTags: ['TaskDefinitions', 'Forms'],
    }),
    getUserReferencedFormsV2: builder.query<
      GetListResponseOfUserReferencedFormResponse,
      UserReferencedFormsRequest
    >({
      queryFn: clientQuery(params =>
        formApiClient.getUserReferencedFormsV2(params)
      ),
      providesTags: ['Forms'],
    }),
    getForm: builder.query<FormResponse, number>({
      queryFn: clientQuery(id => formApiClient.get(id)),
      providesTags: ['Forms'],
    }),
    createForm: builder.mutation<FormResponse, OfflineFormCreateRequest>({
      queryFn: createFormMutationFn,
      invalidatesTags: result =>
        isOfflineQueuedResult(result)
          ? []
          : ['Forms', 'TaskAssignments', 'CourseSessions', 'CourseAssignments'],
    }),
    updateForm: builder.mutation<FormResponse, FormRequest>({
      queryFn: clientQuery(request => formApiClient.update(request)),
      invalidatesTags: [
        'Forms',
        'TaskAssignments',
        'CourseSessions',
        'CourseAssignments',
      ],
    }),
    exportFormSubmissions: builder.mutation<
      FileResponse | null,
      FormSubmissionExportRequest
    >({
      queryFn: clientQuery(request => formApiClient.exportSubmissions(request)),
    }),
    getFormPackageManifest: builder.query<FormPackageManifestResponse, number>({
      queryFn: async formId => {
        try {
          const token = (await fetchAuthSession())?.tokens?.accessToken;
          const response = await axios.post<FormPackageManifestResponse>(
            `${import.meta.env.VITE_API}form/${formId}/package/manifest`,
            null,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                email: localStorage.getItem('email'),
              },
            }
          );
          return {data: response.data};
        } catch (error) {
          return {error};
        }
      },
    }),
    getFormPdf: builder.mutation<
      FileResponse | null,
      {id: number; timeZone: string}
    >({
      queryFn: clientQuery(({id, timeZone}) =>
        formApiClient.getPdf(id, timeZone)
      ),
    }),
    packageForm: builder.mutation<FileResponse | null, PackageFormRequest>({
      queryFn: clientQuery(({formId, timeZone, ...request}: PackageFormRequest) =>
        formApiClient.buildPackage(formId, request, timeZone)
      ),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetFormListQuery,
  useLazyGetFormListQuery,
  useGetFormListV2Query,
  useGetFormRelatedTasksListV2Query,
  useGetUserReferencedFormsV2Query,
  useGetFormQuery,
  useLazyGetFormQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useExportFormSubmissionsMutation,
  useGetFormPackageManifestQuery,
  useGetFormPdfMutation,
  usePackageFormMutation,
} = formApi;
